/**
 * Admin → Ask ABHI.
 *
 * Three questions an admin has about the assistant, top to bottom:
 *   1. Is it being used, and is it working?  KPI tiles + questions per day.
 *   2. Who is using it?                      Per-person usage table.
 *   3. What went wrong?                      Review queue of thumbs-downs, withheld
 *                                            and errored answers (and thumbs-ups),
 *                                            each opening the full question, answer,
 *                                            data lookups and number checks.
 *
 * Admins can read every question and its answer (the chat tells users that
 * questions are reviewed to improve future answers); the review queue lists
 * only answers that were rated or failed.
 */

import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { type ComponentType, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  REASON_LABELS,
  type ChatDayUsage,
  type ChatOverview,
  useChatOverview,
  useChatReviewDetail,
  useMarkReviewed,
} from '../../api/adminChat';
import { ChatMarkdown } from '../../components/chat/ChatMarkdown';
import { ThemeProvider, createTheme, type Theme } from '@mui/material/styles';
import { type ActivityFocus, AskAbhiActivity, StatusChip } from './AskAbhiActivity';
import {
  FONT,
  GOLD,
  GOLD_BUTTON,
  GOLD_TINT,
  INK,
  IVORY_LIGHT,
  LINE,
  MUTED,
  SUBTLE,
  SURFACE,
  card,
  eyebrow,
  sectionTitle,
  when,
} from './askAbhiAdminTheme';

const PERIODS = [7, 30, 90] as const;

/** Answer markdown as one plain line: no citations, markers, tables or emphasis. */
function plainText(md: string): string {
  return md
    .replace(/\[\[[A-Z_]+\]\][^\n]*/g, '')
    .replace(/\[[SN]\d+(?:,\s*[SN]\d+)*\]/g, '')
    .replace(/^:::\w*\s*$/gm, '')
    .replace(/^\s*(?:#{1,6}|[-*•]|\d+[.)])\s+/gm, '')
    .replace(/[*_`|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function pct(part: number, whole: number): string {
  return whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—';
}

function seconds(ms: number | null): string {
  return ms === null ? '—' : `${(ms / 1000).toFixed(ms < 10000 ? 1 : 0)}s`;
}

/** The site's primary button (Create user, etc.). */
const primaryButton = {
  background: GOLD_BUTTON,
  color: INK,
  fontFamily: FONT,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  fontSize: 12,
  px: 2,
  boxShadow: 'none',
  '&:hover': { boxShadow: '0 2px 8px rgba(187, 140, 63, 0.3)', background: GOLD_BUTTON },
} as const;

/** Gold filter chips, the same look as the Audit Log filters. */
function ChipGroup<T extends string | number>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <Box role="radiogroup" aria-label={label} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <Box
            key={String(opt.value)}
            role="radio"
            aria-checked={active}
            tabIndex={0}
            onClick={() => onChange(opt.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onChange(opt.value);
              }
            }}
            sx={{
              cursor: 'pointer',
              userSelect: 'none',
              px: 1.25,
              py: 0.65,
              borderRadius: 1.25,
              fontFamily: FONT,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.02em',
              transition: 'background-color 140ms ease, border-color 140ms ease',
              '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: 2 },
              ...(active
                ? {
                    background: GOLD_BUTTON,
                    color: INK,
                    border: '1px solid transparent',
                    boxShadow: '0 2px 8px rgba(187, 140, 63, 0.20)',
                  }
                : {
                    bgcolor: 'rgba(255, 250, 235, 0.55)',
                    color: 'rgba(15, 10, 7, 0.68)',
                    border: '1px solid rgba(15, 10, 7, 0.10)',
                    '&:hover': {
                      bgcolor: 'rgba(187, 140, 63, 0.10)',
                      borderColor: 'rgba(187, 140, 63, 0.36)',
                      color: INK,
                    },
                  }),
            }}
          >
            {opt.label}
          </Box>
        );
      })}
    </Box>
  );
}

export function AskAbhiAdminPage() {
  const [days, setDays] = useState<number>(30);
  const [openId, setOpenId] = useState<number | null>(null);
  const [focus, setFocus] = useState<ActivityFocus | null>(null);
  const activityRef = useRef<HTMLDivElement>(null);

  const overview = useChatOverview(days);
  const focusActivity = (next: Omit<ActivityFocus, 'nonce'>) => {
    setFocus({ ...next, nonce: Date.now() });
    activityRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const o = overview.data;
  const replies = o ? o.answered + o.clarify + o.refused + o.withheld + o.errors : 0;
  const rated = o ? o.thumbs_up + o.thumbs_down : 0;

  return (
    <ThemeProvider theme={withThemedTooltips}>
      <Box sx={{ fontFamily: FONT, pb: 6 }}>
        {/* Heading + period */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography sx={eyebrow}>AI assistant</Typography>
            <Typography sx={{ ...sectionTitle, fontSize: 20, mt: 0.25 }}>Ask ABHI</Typography>
            <Typography sx={{ fontSize: 14, color: MUTED, mt: 0.25 }}>
              How the assistant is being used, how people rate it, and answers to review.
            </Typography>
          </Box>
          <ChipGroup
            label="Period"
            value={days}
            onChange={setDays}
            options={PERIODS.map((p) => ({ value: p, label: `Last ${p} Days` }))}
          />
        </Box>

        {/* KPI tiles */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(6, 1fr)',
            },
            gap: 1.5,
            mb: 2,
          }}
        >
          <Kpi
            icon={ChatBubbleOutlineIcon}
            label="Questions"
            value={o?.questions}
            sub={
              o
                ? `in ${o.conversations} chat${o.conversations === 1 ? '' : 's'} · ${Math.max(0, o.questions - o.conversations)} follow-up${o.questions - o.conversations === 1 ? '' : 's'}`
                : undefined
            }
            hint="Every message sent to Ask ABHI. A chat is one thread (New chat starts another); follow-ups are the extra questions asked inside a chat."
          />
          <Kpi
            icon={GroupOutlinedIcon}
            label="Active Users"
            value={o?.active_users}
            sub={
              o && o.active_users
                ? `${(o.questions / o.active_users).toFixed(1)} questions each`
                : undefined
            }
          />
          <Kpi
            icon={CheckCircleOutlineIcon}
            label="Answered"
            value={o ? pct(o.answered + o.clarify, replies) : undefined}
            sub={o ? `${o.refused} out of scope · ${o.errors} errors` : undefined}
          />
          <Kpi
            icon={ThumbUpAltOutlinedIcon}
            label="Satisfaction"
            value={o ? pct(o.thumbs_up, rated) : undefined}
            sub={
              o
                ? `${o.thumbs_up} up · ${o.thumbs_down} down · ${pct(rated, replies)} of answers rated`
                : undefined
            }
          />
          <Kpi
            icon={ShieldOutlinedIcon}
            label="Held Back"
            value={o?.withheld}
            sub="Numbers didn't match the data"
            hint="Answers Ask ABHI didn't show: a figure in its draft didn't match the data even after two rewrites, so the person saw a 'couldn't verify' note and the data sources instead of a possibly wrong number."
            onClick={() => focusActivity({ tab: 'review', kind: 'withheld', unreviewed: false })}
          />
          <Kpi
            icon={ThumbDownAltOutlinedIcon}
            label="To Review"
            value={o?.needs_review}
            sub="Need attention, not reviewed"
            hint="Answers rated down, held back, that errored, or that only passed after a rewrite — and that no admin has marked reviewed yet. Thumbs-ups don't need review."
            tone={o && o.needs_review > 0 ? 'alert' : undefined}
            onClick={() => focusActivity({ tab: 'review', kind: 'all', unreviewed: true })}
          />
        </Box>

        {/* Usage over time + reasons */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
            gap: 1.5,
            mb: 3,
          }}
        >
          <Box sx={{ ...card, p: 2.25, display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                flexWrap: 'wrap',
                columnGap: 1.5,
                rowGap: 0.5,
                mb: 1.5,
              }}
            >
              <Box>
                <Typography sx={eyebrow}>Usage</Typography>
                <Typography sx={sectionTitle}>Questions Per Day</Typography>
              </Box>
              {o && (
                <Typography sx={{ fontSize: 12, color: SUBTLE }}>
                  <TimerOutlinedIcon sx={{ fontSize: 13, verticalAlign: '-2px', mr: 0.25 }} />
                  median answer {seconds(o.median_latency_ms)} · avg {seconds(o.avg_latency_ms)}
                </Typography>
              )}
            </Box>
            {o ? <DailyChart data={o.daily} /> : <Skeleton variant="rounded" height={220} />}
          </Box>
          <Box sx={{ display: 'grid', gap: 1.5, alignContent: 'start' }}>
            <CostCard o={o} days={days} />
            <Box sx={{ ...card, p: 2.25 }}>
              <Typography sx={eyebrow}>Feedback</Typography>
              <Typography sx={{ ...sectionTitle, mb: 0.25 }}>
                Why Answers Got a Thumbs-Down
              </Typography>
              <Typography sx={{ fontSize: 12, color: SUBTLE, mb: 1.5 }}>
                Reasons people picked, last {days} days
              </Typography>
              {o ? <Reasons reasons={o.reasons} /> : <Skeleton variant="rounded" height={160} />}
            </Box>
          </Box>
        </Box>

        {/* Questions · People · Review · Topics */}
        <Box ref={activityRef} sx={{ scrollMarginTop: 80 }}>
          <AskAbhiActivity days={days} overview={o} focus={focus} onOpen={setOpenId} />
        </Box>

        <ReviewDrawer id={openId} onClose={() => setOpenId(null)} />
      </Box>
    </ThemeProvider>
  );
}

/**
 * Tooltips in the site theme instead of MUI's near-black: ivory card, gold
 * hairline border, ink text. Scoped to this page (drawer included).
 */
function withThemedTooltips(outer: Theme): Theme {
  return createTheme(outer, {
    components: {
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: IVORY_LIGHT,
            color: INK,
            border: '1px solid rgba(187, 140, 63, 0.28)',
            borderRadius: 10,
            boxShadow: '0 10px 28px rgba(15, 10, 7, 0.14)',
            fontFamily: FONT,
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.45,
            padding: '7px 10px',
            maxWidth: 320,
          },
          arrow: {
            color: IVORY_LIGHT,
            '&::before': { border: '1px solid rgba(187, 140, 63, 0.28)' },
          },
        },
      },
    },
  });
}

// ---------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------
function Kpi({
  icon: Icon,
  label,
  value,
  sub,
  hint,
  tone,
  onClick,
}: {
  icon: ComponentType<{ sx?: object }>;
  label: string;
  value: number | string | undefined;
  sub?: string;
  /** Plain-English definition, shown on hover. */
  hint?: string;
  tone?: 'alert';
  onClick?: () => void;
}) {
  return (
    <Box
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        ...card,
        p: 1.75,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color 150ms ease, box-shadow 150ms ease',
        ...(tone === 'alert' && { borderColor: 'rgba(185, 28, 28, 0.35)' }),
        '&:hover': onClick ? { borderColor: 'rgba(15, 10, 7, 0.22)' } : undefined,
        '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: 2 },
      }}
    >
      <Tooltip title={hint ?? ''} placement="top-start">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 'fit-content' }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: tone === 'alert' ? 'rgba(185, 28, 28, 0.1)' : GOLD_TINT,
              color: tone === 'alert' ? '#b91c1c' : GOLD,
            }}
          >
            <Icon sx={{ fontSize: 14 }} />
          </Box>
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: 'rgba(15, 10, 7, 0.55)',
            }}
          >
            {label}
          </Typography>
        </Box>
      </Tooltip>
      <Typography
        sx={{
          mt: 0.75,
          fontSize: 26,
          fontWeight: 700,
          letterSpacing: '-0.02em',
          lineHeight: 1.1,
          color: tone === 'alert' ? '#b91c1c' : INK,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value === undefined ? <Skeleton width={48} /> : value}
      </Typography>
      <Typography
        title={sub}
        sx={{
          mt: 0.5,
          fontSize: 12,
          lineHeight: 1.4,
          color: SUBTLE,
          minHeight: 18,
          // Two lines on phones, one (with ellipsis) where there's room.
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: { xs: 2, sm: 1 },
          overflow: 'hidden',
        }}
      >
        {sub}
      </Typography>
    </Box>
  );
}

function DailyChart({ data }: { data: ChatDayUsage[] }) {
  const rows = useMemo(
    () =>
      data.map((d) => ({
        ...d,
        label: new Date(`${d.day}T12:00:00`).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
        }),
      })),
    [data],
  );
  if (!rows.some((r) => r.questions > 0)) {
    return (
      <Box sx={{ height: 220, display: 'grid', placeItems: 'center', color: SUBTLE, fontSize: 14 }}>
        No questions in this period yet.
      </Box>
    );
  }
  return (
    <Box sx={{ flex: 1, minHeight: 220 }} role="img" aria-label="Questions asked per day">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={rows}
          margin={{ top: 4, right: 4, bottom: 0, left: -18 }}
          barCategoryGap="22%"
        >
          <CartesianGrid vertical={false} stroke="rgba(15, 10, 7, 0.06)" />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
            minTickGap={24}
            tick={{ fontSize: 11, fill: 'rgba(15, 10, 7, 0.5)' }}
          />
          <YAxis
            allowDecimals={false}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: 'rgba(15, 10, 7, 0.5)' }}
          />
          <RTooltip
            cursor={{ fill: 'rgba(187, 140, 63, 0.08)' }}
            content={({ active, payload }) => {
              const d = active
                ? (payload?.[0]?.payload as (typeof rows)[number] | undefined)
                : undefined;
              if (!d) return null;
              return (
                <Box sx={{ ...card, px: 1.5, py: 1, fontSize: 12, minWidth: 160 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: INK, mb: 0.5 }}>
                    {d.label} · {d.questions} question{d.questions === 1 ? '' : 's'}
                  </Typography>
                  {(
                    [
                      ['Answered', d.answered],
                      ['Out of Scope', d.refused],
                      ['Held Back', d.withheld],
                      ['Errors', d.errors],
                    ] as const
                  ).map(([k, v]) => (
                    <Box
                      key={k}
                      sx={{ display: 'flex', justifyContent: 'space-between', color: MUTED }}
                    >
                      <span>{k}</span>
                      <b style={{ color: INK, fontVariantNumeric: 'tabular-nums' }}>{v}</b>
                    </Box>
                  ))}
                </Box>
              );
            }}
          />
          <Bar dataKey="questions" fill={GOLD} radius={[4, 4, 0, 0]} maxBarSize={28} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

function money(usd: number): string {
  if (usd > 0 && usd < 0.01) return '<$0.01';
  return usd.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * What the assistant costs, in dollars: tokens are what Anthropic bills, but
 * nobody budgets in tokens. Priced per model and per token kind on the server.
 */
function CostCard({ o, days }: { o: ChatOverview | undefined; days: number }) {
  return (
    <Box sx={{ ...card, p: 2.25 }}>
      <Typography sx={eyebrow}>Cost</Typography>
      <Typography sx={sectionTitle}>Claude API Spend</Typography>
      {!o ? (
        <Skeleton variant="rounded" height={96} sx={{ mt: 1.5 }} />
      ) : (
        <>
          <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography
              sx={{
                fontSize: 30,
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: INK,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {money(o.cost_usd)}
            </Typography>
            <Typography sx={{ fontSize: 13, color: SUBTLE }}>last {days} days</Typography>
          </Box>
          <Box
            sx={{
              mt: 1.25,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1,
            }}
          >
            {(
              [
                [
                  'Per Question',
                  o.cost_per_question_usd === null ? '—' : money(o.cost_per_question_usd),
                ],
                ['Monthly Pace', `${money(o.cost_month_pace_usd)}/mo`],
              ] as const
            ).map(([k, v]) => (
              <Box
                key={k}
                sx={{ px: 1.25, py: 1, borderRadius: '10px', bgcolor: 'rgba(187, 140, 63, 0.07)' }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: MUTED }}>{k}</Typography>
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: INK,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {v}
                </Typography>
              </Box>
            ))}
          </Box>
          <Tooltip
            title="Tokens are the units Anthropic bills by — roughly ¾ of a word each. Each answer reads the instructions, the question and the data it looked up (input), then writes the reply (output). Output costs about 5× input."
            placement="bottom-start"
          >
            <Typography
              sx={{
                mt: 1.25,
                fontSize: 12,
                color: SUBTLE,
                width: 'fit-content',
                borderBottom: `1px dotted ${SUBTLE}`,
                cursor: 'help',
              }}
            >
              {(o.input_tokens + o.output_tokens).toLocaleString()} tokens ·{' '}
              {o.questions
                ? `~${Math.round((o.input_tokens + o.output_tokens) / o.questions).toLocaleString()} per question`
                : 'no questions yet'}
            </Typography>
          </Tooltip>
          {o.cost_estimated && (
            <Typography sx={{ mt: 0.75, fontSize: 11.5, color: SUBTLE, lineHeight: 1.45 }}>
              Includes estimates for answers from before exact costs were recorded (priced from
              their token counts, so slightly high).
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

function Reasons({ reasons }: { reasons: { reason: string; count: number }[] }) {
  return (
    <BarList
      items={reasons.map((r) => ({
        key: r.reason,
        label: REASON_LABELS[r.reason] ?? r.reason,
        count: r.count,
      }))}
      empty="No thumbs-downs in this period."
    />
  );
}

/** Ranked list with a thin gold bar per row — one hue, length carries the value. */
function BarList({
  items,
  empty,
}: {
  items: { key: string; label: string; count: number }[];
  empty: string;
}) {
  if (!items.length) {
    return <Typography sx={{ fontSize: 13, color: SUBTLE }}>{empty}</Typography>;
  }
  const max = Math.max(...items.map((r) => r.count));
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {items.map((r) => (
        <Box key={r.key}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, mb: 0.5 }}>
            <span style={{ color: INK }}>{r.label}</span>
            <b style={{ color: INK, fontVariantNumeric: 'tabular-nums' }}>{r.count}</b>
          </Box>
          <Box sx={{ height: 6, borderRadius: 999, bgcolor: GOLD_TINT, overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%',
                width: `${(r.count / max) * 100}%`,
                bgcolor: GOLD,
                borderRadius: 999,
              }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function DrawerSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box sx={{ mt: 2.5 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: SUBTLE,
          mb: 1,
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function ReviewDrawer({ id, onClose }: { id: number | null; onClose: () => void }) {
  const isMobile = useMediaQuery('(max-width:600px)');
  const detail = useChatReviewDetail(id);
  const mark = useMarkReviewed();
  const [note, setNote] = useState('');
  const d = detail.data;
  useEffect(() => setNote(d?.review_note ?? ''), [d?.message_id, d?.review_note]);
  const sources = useMemo(
    () => new Map((d?.citations ?? []).map((c) => [c.id, c])),
    [d?.citations],
  );
  const numbers = useMemo(
    () => new Map((d?.citations ?? []).map((c, i) => [c.id, i + 1])),
    [d?.citations],
  );

  return (
    <Drawer
      anchor="right"
      open={id !== null}
      onClose={onClose}
      // Above the Ask ABHI launcher and Feedback pill (z 1250), below the chat panel.
      sx={{ zIndex: 1300 }}
      PaperProps={{ sx: { width: isMobile ? '100%' : 600, fontFamily: FONT } }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2.5,
          py: 1.5,
          borderBottom: `1px solid ${LINE}`,
        }}
      >
        <Typography sx={{ ...sectionTitle, flex: 1 }}>Answer Review</Typography>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      {!d ? (
        <Box sx={{ p: 4, display: 'grid', placeItems: 'center' }}>
          {detail.isError ? (
            <Typography sx={{ color: MUTED }}>Couldn't load this answer.</Typography>
          ) : (
            <CircularProgress size={22} />
          )}
        </Box>
      ) : (
        <Box sx={{ px: 2.5, pb: 4, overflowY: 'auto' }}>
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 14, fontWeight: 650 }}>{d.user.name}</Typography>
            <Typography sx={{ fontSize: 12, color: SUBTLE }}>{d.user.email}</Typography>
            <Box sx={{ flex: 1 }} />
            <StatusChip status={d.status} />
            <Typography sx={{ fontSize: 12, color: SUBTLE }}>{when(d.created_at)}</Typography>
          </Box>

          {d.earlier.length > 0 && (
            <DrawerSection title={`Earlier in This Conversation (${d.earlier.length})`}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {d.earlier.map((t, i) => (
                  <Typography
                    key={i}
                    sx={{
                      fontSize: 12,
                      color: t.role === 'user' ? INK : MUTED,
                      fontWeight: t.role === 'user' ? 600 : 400,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {t.role === 'user' ? 'Q: ' : 'A: '}
                    {plainText(t.content)}
                  </Typography>
                ))}
              </Box>
            </DrawerSection>
          )}

          <DrawerSection title="Question">
            <Box sx={{ px: 1.5, py: 1, borderRadius: '10px', bgcolor: SURFACE, fontSize: 14 }}>
              {d.question ?? '—'}
            </Box>
          </DrawerSection>

          <DrawerSection title="Answer Shown">
            <Box sx={{ ...card, p: 1.75 }}>
              <ChatMarkdown
                text={d.answer}
                citeNumbers={numbers}
                sources={sources}
                onCite={() => undefined}
              />
            </Box>
          </DrawerSection>

          {(d.rating !== null || d.comment) && (
            <DrawerSection title="Their Feedback">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                {d.rating === 1 && (
                  <Chip
                    size="small"
                    icon={<ThumbUpAltOutlinedIcon />}
                    label="Good Response"
                    color="success"
                    variant="outlined"
                  />
                )}
                {d.rating === -1 && (
                  <Chip
                    size="small"
                    icon={<ThumbDownAltOutlinedIcon />}
                    label="Bad Response"
                    color="error"
                    variant="outlined"
                  />
                )}
                {d.reasons.map((x) => (
                  <Chip key={x} size="small" label={REASON_LABELS[x] ?? x} />
                ))}
              </Box>
              {d.comment && (
                <Typography sx={{ mt: 1, fontSize: 14, color: INK, fontStyle: 'italic' }}>
                  “{d.comment}”
                </Typography>
              )}
            </DrawerSection>
          )}

          <DrawerSection title="How It Answered">
            {d.tool_calls.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: MUTED }}>
                No data lookups (general or out-of-scope reply).
              </Typography>
            ) : (
              <Box
                component="ol"
                sx={{ m: 0, pl: 2.25, display: 'flex', flexDirection: 'column', gap: 0.75 }}
              >
                {d.tool_calls.map((c, i) => (
                  <Box component="li" key={i} sx={{ fontSize: 13 }}>
                    <Tooltip
                      title={
                        <pre style={{ margin: 0, fontSize: 11 }}>
                          {JSON.stringify(c.input, null, 2)}
                        </pre>
                      }
                    >
                      <span
                        style={{
                          color: c.failed ? '#b91c1c' : INK,
                          borderBottom: `1px dotted ${SUBTLE}`,
                        }}
                      >
                        {c.label}
                      </span>
                    </Tooltip>
                    <Box component="span" sx={{ color: SUBTLE, ml: 0.75 }}>
                      {c.failed ? 'failed' : `${c.rows ?? 0} rows`} · {seconds(c.ms)}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </DrawerSection>

          <DrawerSection title="Number Check">
            <Typography sx={{ fontSize: 13 }}>
              {d.numbers_checked} figure{d.numbers_checked === 1 ? '' : 's'} checked
              {d.retries ? ` · ${d.retries} rewrite${d.retries === 1 ? '' : 's'}` : ''}
              {d.unmatched_numbers.length > 0 && (
                <Box component="span" sx={{ color: '#b45309' }}>
                  {' '}
                  · didn't match: {d.unmatched_numbers.join(', ')}
                </Box>
              )}
            </Typography>
            {d.rejected_drafts.map((draft, i) => (
              <Box
                key={i}
                component="details"
                sx={{ mt: 1, fontSize: 12, '& summary': { cursor: 'pointer', color: MUTED } }}
              >
                <summary>
                  Draft {i + 1} held back — {draft.problems[0]?.slice(0, 90)}…
                </summary>
                <Box
                  sx={{
                    mt: 0.75,
                    p: 1.25,
                    borderRadius: '8px',
                    bgcolor: SURFACE,
                    whiteSpace: 'pre-wrap',
                    color: MUTED,
                  }}
                >
                  {draft.text}
                </Box>
              </Box>
            ))}
          </DrawerSection>

          <Typography sx={{ mt: 2, fontSize: 12, color: SUBTLE }}>
            {d.model ?? 'unknown model'} · {seconds(d.latency_ms)} ·{' '}
            {((d.input_tokens ?? 0) + (d.output_tokens ?? 0)).toLocaleString()} tokens
          </Typography>

          <DrawerSection title="Review">
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              placeholder="What was wrong, what you fixed or who you told (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 1000))}
            />
            <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ flex: 1, fontSize: 12, color: SUBTLE }}>
                {d.reviewed_at
                  ? `Reviewed by ${d.reviewed_by ?? 'an admin'} · ${when(d.reviewed_at)}`
                  : 'Not reviewed yet'}
              </Typography>
              {d.reviewed_at && (
                <Button
                  size="small"
                  disabled={mark.isPending}
                  onClick={() => mark.mutate({ id: d.message_id, reviewed: false })}
                  sx={{
                    fontFamily: FONT,
                    fontWeight: 600,
                    fontSize: 12,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: MUTED,
                  }}
                >
                  Mark Not Reviewed
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                disableElevation
                disabled={mark.isPending}
                onClick={() => mark.mutate({ id: d.message_id, reviewed: true, note })}
                sx={primaryButton}
              >
                {d.reviewed_at ? 'Update Review' : 'Mark Reviewed'}
              </Button>
            </Box>
            {mark.isError && (
              <Typography sx={{ mt: 1, fontSize: 12, color: '#b91c1c' }}>
                <ErrorOutlineIcon sx={{ fontSize: 13, verticalAlign: '-2px', mr: 0.5 }} />
                Couldn't save the review. Please try again.
              </Typography>
            )}
          </DrawerSection>
        </Box>
      )}
    </Drawer>
  );
}
